// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { getReferenceString } from '@medplum/core';
import type { Composition, DiagnosticReport, Patient } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { useCallback, useEffect, useState } from 'react';
import { POLARIS_COMPOSITION_TYPE_SYSTEM } from '../constants/medical-info';

const INTAKE_TYPE_CODES = ['self-referral-intake', 'pcp-referral-intake']
  .map((code) => `${POLARIS_COMPOSITION_TYPE_SYSTEM}|${code}`)
  .join(',');

export interface UseSupplementalInformationResult {
  intakeComposition: Composition | undefined;
  hsatDiagnosticReport: DiagnosticReport | undefined;
  loading: boolean;
  error: Error | null;
  refresh: () => void;
}

export function useSupplementalInformation(patient: Patient | undefined): UseSupplementalInformationResult {
  const medplum = useMedplum();
  const [intakeComposition, setIntakeComposition] = useState<Composition | undefined>();
  const [hsatDiagnosticReport, setHsatDiagnosticReport] = useState<DiagnosticReport | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetch = useCallback(async () => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [comp, report] = await Promise.all([
        medplum
          .searchOne('Composition', {
            subject: getReferenceString(patient),
            type: INTAKE_TYPE_CODES,
            _sort: '-date',
          })
          .catch(() => undefined),
        medplum
          .searchOne('DiagnosticReport', {
            subject: getReferenceString(patient),
            _sort: '-date',
          })
          .catch(() => undefined),
      ]);
      setIntakeComposition(comp);
      setHsatDiagnosticReport(report);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load supplemental information'));
    } finally {
      setLoading(false);
    }
  }, [medplum, patient]);

  useEffect(() => {
    fetch().catch(console.error);
  }, [fetch]);

  return { intakeComposition, hsatDiagnosticReport, loading, error, refresh: fetch };
}
